import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, formatApiError } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Eye, EyeSlash, SpinnerGap, CheckCircle, EnvelopeSimple } from "@phosphor-icons/react";
import { KNORIA_LOGO_URL } from "../components/KnoriaLogo";
import { PublicLangToggle } from "../components/PublicLangToggle";

const HERO_IMAGE = "https://static.prod-images.emergentagent.com/jobs/9ce68c36-1b03-4868-a37f-38db73ced2af/images/6c23dca8692d1d393927065ec04d86a741ae0e4f93a6bda3268d58f58d6b9432.png";
const API = process.env.REACT_APP_BACKEND_URL;

export default function RegisterPage() {
  const { lang, t } = useLang();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [registrationReason, setRegistrationReason] = useState("");
  const [partnerCode, setPartnerCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingSuccess, setPendingSuccess] = useState(false);
  // Vincent 2026-06-03 (Eric — items 12, 13) — Token d'invitation détecté depuis l'URL
  // + métadonnées récupérées du backend pour afficher un bandeau de bienvenue.
  const [inviteInfo, setInviteInfo] = useState(null);
  const inviteToken = searchParams.get("invite") || "";
  const { register, user, logout } = useAuth();

  // Eric iter83 — SECURITY: si un founder/admin déjà connecté ouvre un lien
  // d'invitation, on l'oblige à se déconnecter avant de créer le compte de
  // l'invité. Sinon le cookie de session écrase l'identité du nouveau compte.
  const sessionConflict = !!user && !!inviteToken && user.email !== (inviteInfo?.email || "");

  // Pré-remplissage depuis query params (lien d'invitation) au premier render.
  useEffect(() => {
    const qpEmail = searchParams.get("email");
    const qpName = searchParams.get("name");
    const qpCoupon = searchParams.get("partner_code");
    if (qpEmail) setEmail(qpEmail);
    if (qpName) setName(qpName);
    if (qpCoupon) setPartnerCode(qpCoupon.toUpperCase());
    // Si un token d'invitation est présent, on fetch les détails pour afficher
    // un bandeau "Vous êtes invité par X à rejoindre Y en tant que Z".
    if (inviteToken) {
      axios.get(`${API}/api/organizations/invite/${inviteToken}`)
        .then(({ data }) => {
          setInviteInfo(data);
          if (data?.email && !qpEmail) setEmail(data.email);
          if (data?.name && !qpName) setName(data.name);
          if (data?.partner_code && !qpCoupon) setPartnerCode(String(data.partner_code).toUpperCase());
          // Eric iter83 — Pré-remplir AUSSI le nom d'entreprise depuis l'invitation
          // (provient du nom de l'org qui invite). Évite à l'invité de retaper
          // une compagnie potentiellement différente de celle qui l'invite.
          if (data?.org_name) setCompanyName(data.org_name);
        })
        .catch(() => { /* token invalide ou expiré — l'utilisateur verra l'erreur au submit */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (sessionConflict) {
      setError(lang === "fr"
        ? "Vous devez vous déconnecter avant de créer ce compte invité."
        : "You must log out before creating this invited account.");
      return;
    }
    setLoading(true);
    try {
      // Vincent 2026-06-03 (Eric — item 13) — Si un token d'invitation est présent,
      // le passer au backend pour qu'il rattache l'utilisateur directement à l'org
      // sans passer par "pending_approval".
      const result = await register(
        email, password, name,
        partnerCode || undefined,
        companyName || undefined,
        registrationReason || undefined,
        inviteToken || undefined,
      );
      if (result.account_status === "pending_approval") {
        setPendingSuccess(true);
      } else {
        // Eric iter83b — Si inscription via invitation OK, navigation explicite
        // vers dashboard (PublicRoute n'auto-redirige pas quand un invite token
        // est dans l'URL, pour éviter les bypass de session).
        navigate("/dashboard", { replace: true });
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-zinc-900">
        <img src={HERO_IMAGE} alt="AI Pipeline" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-900/80 to-zinc-900/40" />
        <div className="relative z-10 p-12 flex flex-col justify-between h-full">
          <div className="flex items-center gap-3">
            <img src={KNORIA_LOGO_URL} alt="KNORIA" className="h-10 object-contain" />
            <span className="text-white text-xl font-semibold tracking-tight font-['Outfit']">KNORIA</span>
          </div>
          <div>
            <h1 className="text-4xl lg:text-5xl font-semibold text-white tracking-tight font-['Outfit'] leading-tight">{t("register_subtitle")}</h1>
          </div>
          <div className="text-zinc-500 text-sm">&copy; 2026 KNORIA. Knowledge Empowered.</div>
        </div>
      </div>
      <div className="relative flex-1 flex items-center justify-center p-8 bg-[#FAFAFA]">
        <div className="absolute top-3 right-3">
          <PublicLangToggle tone="light" />
        </div>
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src={KNORIA_LOGO_URL} alt="KNORIA" className="h-10 object-contain" />
            <span className="text-xl font-semibold tracking-tight font-['Outfit']">KNORIA</span>
          </div>

          {sessionConflict && !pendingSuccess && (
            <div className="mb-6 rounded-lg border-2 border-amber-300 bg-amber-50 p-4" data-testid="session-conflict-banner">
              <p className="text-sm font-semibold text-amber-900 mb-2">
                {lang === "fr" ? "⚠️ Session active détectée" : "⚠️ Active session detected"}
              </p>
              <p className="text-xs text-amber-800 mb-3">
                {lang === "fr"
                  ? `Vous êtes connecté en tant que ${user.email}. Cette invitation cible un autre courriel (${inviteInfo?.email || "—"}). Déconnectez-vous d'abord pour créer le compte invité.`
                  : `You're logged in as ${user.email}. This invitation targets a different email (${inviteInfo?.email || "—"}). Log out first to create the invited account.`}
              </p>
              <Button
                type="button"
                onClick={async () => { await logout(); }}
                className="h-9 px-4 bg-amber-600 hover:bg-amber-500 text-white text-xs"
                data-testid="logout-before-invite-btn"
              >
                {lang === "fr" ? "Se déconnecter maintenant" : "Log out now"}
              </Button>
            </div>
          )}

          {inviteInfo && !pendingSuccess && (
            <div className="mb-6 rounded-lg border-2 border-blue-200 bg-blue-50 p-4 flex items-start gap-3" data-testid="invite-banner">
              <EnvelopeSimple size={22} className="text-blue-600 shrink-0 mt-0.5" weight="duotone" />
              <div className="text-sm">
                <p className="font-semibold text-blue-900">
                  {lang === "fr" ? "Vous êtes invité·e à rejoindre " : "You're invited to join "}
                  <strong>{inviteInfo.org_name}</strong>
                </p>
                <p className="text-blue-700 text-xs mt-1">
                  {lang === "fr"
                    ? `Par ${inviteInfo.invited_by || "un administrateur"} · Rôle attribué : `
                    : `By ${inviteInfo.invited_by || "an administrator"} · Assigned role: `}
                  <strong>{
                    {
                      viewer: lang === "fr" ? "Visiteur" : "Viewer",
                      employee: lang === "fr" ? "Employé" : "Employee",
                      employee_new: lang === "fr" ? "Nouvel employé" : "New employee",
                      employee_trained: lang === "fr" ? "Employé formé" : "Trained employee",
                      employee_advanced: lang === "fr" ? "Employé avancé" : "Advanced employee",
                      concepteur: lang === "fr" ? "Concepteur" : "Designer",
                      admin: "Admin", founder: lang === "fr" ? "Fondateur" : "Founder",
                    }[inviteInfo.role] || inviteInfo.role
                  }</strong>
                </p>
              </div>
            </div>
          )}

          {pendingSuccess ? (
            <div className="text-center animate-fade-in" data-testid="pending-success-screen">
              <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={36} className="text-teal-600" />
              </div>
              <h2 className="text-2xl font-semibold tracking-tight font-['Outfit'] mb-3">
                {lang === "en" ? "Request Submitted!" : "Demande envoyée!"}
              </h2>
              <p className="text-zinc-600 mb-2">
                {lang === "en"
                  ? "Your access request has been sent to the KNORIA administrators."
                  : "Votre demande d'accès a été envoyée aux administrateurs KNORIA."}
              </p>
              <p className="text-sm text-zinc-500 mb-6">
                {lang === "en"
                  ? "You will receive an email at the address provided once your account has been approved."
                  : "Vous recevrez un courriel à l'adresse fournie une fois votre compte approuvé."}
              </p>
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800 mb-6">
                {lang === "en"
                  ? "Approval is usually completed within 24 hours."
                  : "L'approbation est généralement complétée en moins de 24 heures."}
              </div>
              <Link to="/login">
                <Button variant="outline" className="w-full" data-testid="back-to-login-btn">
                  {lang === "en" ? "Back to login" : "Retour à la connexion"}
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <span className="overline text-zinc-500">{t("welcome_back")}</span>
                <h2 className="text-3xl font-semibold tracking-tight font-['Outfit'] mt-2">{t("register_title")}</h2>
              </div>
              <form onSubmit={handleSubmit} className="space-y-5">
                {error && <div className="p-4 bg-red-50 border border-red-200 rounded-sm text-red-700 text-sm animate-fade-in" data-testid="register-error">{error}</div>}
                <div className="space-y-2">
                  <Label htmlFor="name">{t("full_name")}</Label>
                  <Input id="name" type="text" value={name} onChange={(e) => setName(e.target.value)} required className="h-11 rounded-sm" data-testid="register-name-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">{t("email_address")}</Label>
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("email_placeholder")} required className="h-11 rounded-sm" data-testid="register-email-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="companyName">
                    {lang === "en" ? "Company name" : "Nom de l'entreprise"}
                    {inviteInfo && (
                      <span className="ml-2 text-xs text-blue-600">
                        {lang === "fr" ? "(fixé par l'invitation)" : "(set by invitation)"}
                      </span>
                    )}
                  </Label>
                  <Input
                    id="companyName" type="text" value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder={lang === "en" ? "Your company or firm" : "Votre entreprise ou cabinet"}
                    required
                    readOnly={!!inviteInfo}
                    className={`h-11 rounded-sm ${inviteInfo ? "bg-zinc-100 cursor-not-allowed" : ""}`}
                    data-testid="register-company-input"
                  />
                </div>
                {/* Vincent iter85 (Eric 2026-06-04) — Raison MASQUÉE si invite token
                    présent : un user invité par un admin/founder est déjà approuvé,
                    pas besoin de justifier son accès. Friction inutile pour Vincent
                    et le nouveau programmeur. */}
                {!inviteToken && (
                  <div className="space-y-2">
                    <Label htmlFor="registrationReason">{lang === "en" ? "Reason for access" : "Raison de la demande"}</Label>
                    <textarea
                      id="registrationReason"
                      value={registrationReason}
                      onChange={(e) => setRegistrationReason(e.target.value)}
                      placeholder={lang === "en" ? "Briefly describe why you need access to KNORIA..." : "Décrivez brièvement pourquoi vous souhaitez accéder à KNORIA…"}
                      rows={2}
                      required
                      className="w-full px-3 py-2 bg-background border border-input rounded-sm text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                      data-testid="register-reason-input"
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="password">{t("password")}</Label>
                  <div className="relative">
                    <Input id="password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required minLength={8} className="h-11 rounded-sm pr-10" data-testid="register-password-input" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600" data-testid="toggle-password-visibility">
                      {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  <p className="text-xs text-zinc-400">{t("password_requirements")}</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="partnerCode">{lang === "en" ? "Coupon / Partner code" : "Coupon / Code partenaire"} <span className="text-zinc-400 font-normal">({lang === "en" ? "optional" : "optionnel"})</span></Label>
                  <Input id="partnerCode" type="text" value={partnerCode} onChange={(e) => setPartnerCode(e.target.value.toUpperCase())} placeholder={t("partner_code_placeholder")} className="h-11 rounded-sm uppercase tracking-wider font-mono" data-testid="register-partner-code-input" />
                  <p className="text-[11px] text-zinc-500">
                    {lang === "en"
                      ? "If you have a coupon from your employer, paste it here — you will be automatically linked to their organization."
                      : "Si votre employeur vous a fourni un coupon, collez-le ici — vous serez automatiquement rattaché à son entreprise."}
                  </p>
                </div>
                <label className="flex items-start gap-2.5 cursor-pointer" data-testid="accept-terms-label">
                  <input type="checkbox" required className="mt-1 w-4 h-4 rounded border-zinc-300 text-[#002FA7] focus:ring-[#002FA7]" data-testid="accept-terms-checkbox" />
                  <span className="text-xs text-zinc-500">
                    {lang === "en"
                      ? <>I accept the <a href="/terms" target="_blank" className="text-[#002FA7] underline">Terms of Service</a> and <a href="/terms" target="_blank" className="text-[#002FA7] underline">Privacy Policy</a></>
                      : <>J'accepte les <a href="/terms" target="_blank" className="text-[#002FA7] underline">conditions d'utilisation</a> et la <a href="/terms" target="_blank" className="text-[#002FA7] underline">politique de confidentialité</a></>}
                  </span>
                </label>
                <Button type="submit" disabled={loading} className="w-full h-11 rounded-sm bg-[#002FA7] hover:bg-[#002280] text-white font-medium" data-testid="register-submit-btn">
                  {loading ? <SpinnerGap className="animate-spin" size={20} /> : (lang === "en" ? "Submit access request" : "Soumettre la demande d'accès")}
                </Button>
              </form>
              <p className="mt-6 text-center text-sm text-zinc-500">
                {t("already_account")}{" "}
                <Link to="/login" className="text-[#002FA7] hover:underline font-medium" data-testid="login-link">{t("sign_in_link")}</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
